/**
 * §5's fallback-only manual mapping UI (ACTION_PLAN.md E4). Rendered
 * conditionally by GenerateJob.tsx, only when generate/handler.py's
 * response reports `unresolved_fields` — never a default step in the
 * flow (§5/§11): the generation model already made its best-effort
 * mapping and documented its reasoning in the script's own comments,
 * this just lets the caller correct fields it wasn't confident about.
 *
 * A destination-field -> source-field mapping is a free-text input here
 * rather than a drag-connected line between two field lists: generate/
 * handler.py's response carries no parsed source-field list to draw a
 * second column from (schemas/canonical.py's `CanonicalSchema` never
 * leaves the backend, per §8's zero-retention/no-content-in-responses
 * rule) — the same `field_mapping_overrides: dict[str, str]` shape
 * schemas/generation.py's `GenerationOptions` already defines is what
 * this UI collects and resubmits.
 */

import type { ChangeEvent } from 'react';

interface FieldMappingProps {
  unresolvedFields: string[];
  overrides: Record<string, string>;
  onMap: (destinationField: string, sourceField: string) => void;
  onRegenerate: () => void;
  busy: boolean;
}

export function FieldMapping({
  unresolvedFields,
  overrides,
  onMap,
  onRegenerate,
  busy,
}: FieldMappingProps) {
  if (unresolvedFields.length === 0) return null;

  return (
    <section className="field-mapping">
      <h2>Some fields need a manual mapping</h2>
      <p className="muted">
        The generator made its best guess for these fields and documented its reasoning in the
        script&rsquo;s comments, but wasn&rsquo;t confident enough to skip this step. Map each one
        to the correct source field, then regenerate.
      </p>
      <ul className="field-mapping-list">
        {unresolvedFields.map((field) => (
          <li key={field} className="field-mapping-row">
            <span className="field-mapping-target">{field}</span>
            <input
              type="text"
              placeholder="source field path"
              value={overrides[field] ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onMap(field, event.target.value)}
              aria-label={`Source field for ${field}`}
            />
          </li>
        ))}
      </ul>
      <button className="btn btn--primary" disabled={busy} onClick={onRegenerate}>
        {busy ? 'Regenerating…' : 'Regenerate with these mappings'}
      </button>
    </section>
  );
}
